// =============================================================================
// VandieZ shipper-web -- Load detail (/loads/:loadId)
// =============================================================================
// Shows a single load's full details + the bid list + actions:
//   * Accept a bid (locks the load; reveals pickup/drop contact PII; spawns a
//     trip the shipper can then track).
//   * Reject a single bid.
//   * Cancel the entire load (DELETE; may carry a cancellation fee if a bid
//     was already accepted).
//   * Edit owner-editable fields inline (PATCH; locked once accepted).
//
// Status pills are reused from MyLoadsPage. Bids are sorted by amount asc by
// the backend (cheapest first) for shipper view; the UI just renders the
// returned order.
// =============================================================================

import {
  AlertCircle,
  ArrowLeft,
  Ban,
  CalendarClock,
  Check,
  ChevronRight,
  Loader2,
  Pencil,
  ThumbsDown,
  Truck,
} from "lucide-react";
import type React from "react";
import { useEffect, useId, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { bidsApi, loadsApi, postponementApi } from "@/api/client";
import { PageHeader } from "@/components/AppShell";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/auth";
import { ApiError } from "@/types/auth";
import type {
  Bid,
  BidsForLoadResponse,
  Load,
  LoadPatchPayload,
  LoadStatus,
} from "@/types/load";

const STATUS_LABEL: Record<LoadStatus, string> = {
  draft: "Draft",
  open_for_bidding: "Open for bidding",
  accepted: "Accepted",
  completed: "Completed",
  cancelled: "Cancelled",
  expired: "Expired",
  postponed: "Postponed",
};

const STATUS_TONE: Record<LoadStatus, string> = {
  draft: "bg-muted text-muted-foreground",
  open_for_bidding: "bg-success-subtle text-success-subtle-foreground",
  accepted: "bg-primary-subtle text-primary-subtle-foreground",
  completed: "bg-muted text-muted-foreground",
  cancelled: "bg-danger-subtle text-danger-subtle-foreground",
  expired: "bg-muted text-muted-foreground",
  postponed: "bg-warning-subtle text-warning-subtle-foreground",
};

function formatINR(amount: string): string {
  const n = Number.parseFloat(amount);
  if (Number.isNaN(n)) {return amount;}
  return `₹${n.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

// Mirrors the server gate at loads.py:982 exactly.
function isEditable(load: Load): boolean {
  return load.status === "draft" || load.status === "open_for_bidding";
}

// The server cancel gate is a DENYLIST -- it refuses only `cancelled` and
// `expired` (loads.py:1239), which means it would also let a COMPLETED load
// be flipped to cancelled. We do not offer that here: a delivered load is
// history, and turning it into a cancellation is data damage, not a
// workflow. Treated as a server-side gap rather than mirrored into the UI.
//
// `postponed` IS cancellable -- a shipper who has already moved the pickup
// twice and then gives up needs a way out.
function isCancellable(load: Load): boolean {
  return !["cancelled", "expired", "completed"].includes(load.status);
}

/** CU-10 (p20): "sort / filter bids by price, rating, distance".
 *
 * THE DEFAULT IS THE SERVER'S ORDER, AND THAT IS DELIBERATE. The
 * backend does NOT return bids cheapest-first (older copy on this page
 * claimed it did). It applies Spec 6 §5.1 two-bucket ranking —
 * top-rated bidders first, then new drivers, each by quote ascending —
 * and surfaces at most three per page. That ranking is a product
 * decision about who gets seen; re-sorting by default would quietly
 * overrule it, which is what a first version of this control did.
 *
 * So "Recommended" is the default and the others are opt-in. Each of
 * them shows the shipper something the ranking does not:
 *
 *   Cheapest  — across BOTH buckets, so a cheap new driver can surface
 *               above a pricier top-rated one
 *   Arrives soonest — ETA is not an input to the ranking at all
 *   Best rated — largely agrees with the ranking; kept because p20
 *               names it and a shipper should not have to infer it
 *
 * DISTANCE IS NOT ON A BID and is not faked. What a shipper needs from
 * that axis is "who gets here soonest", which `proposed_eta_pickup`
 * answers directly — a nearer driver already loaded elsewhere is not
 * the sooner one.
 *
 * Sorting is client-side over the page already fetched (the server
 * takes no sort parameter), so the label says when there are more.
 */
type BidSort = "recommended" | "price" | "rating" | "eta";

const BID_SORTS: { key: BidSort; label: string }[] = [
  { key: "recommended", label: "Recommended" },
  { key: "price", label: "Cheapest" },
  { key: "rating", label: "Best rated" },
  { key: "eta", label: "Arrives soonest" },
];

function sortBids(items: Bid[], by: BidSort): Bid[] {
  if (by === "recommended") {
    // Untouched: this IS the server's ranked order.
    return items;
  }
  const copy = [...items];
  switch (by) {
    case "rating":
      return copy.sort((a, b) => {
        // A bidder with too few ratings has `avg_rating: null` (the
        // server withholds it below three). Unrated sorts LAST rather
        // than as zero — untested is not the same as bad, and a new
        // driver should not be buried beneath a poorly-rated one.
        const ar = a.bidder_stats?.avg_rating ?? -1;
        const br = b.bidder_stats?.avg_rating ?? -1;
        return br - ar;
      });
    case "eta":
      return copy.sort(
        (a, b) =>
          new Date(a.proposed_eta_pickup).getTime() -
          new Date(b.proposed_eta_pickup).getTime(),
      );
    default:
      return copy.sort(
        (a, b) => Number.parseFloat(a.bid_amount) - Number.parseFloat(b.bid_amount),
      );
  }
}

export function LoadDetailPage(): React.JSX.Element {
  const [bidSort, setBidSort] = useState<BidSort>("recommended");
  const { loadId } = useParams<{ loadId: string }>();
  const navigate = useNavigate();
  const { accessToken } = useAuth();

  const [load, setLoad] = useState<Load | null>(null);
  const [bids, setBids] = useState<BidsForLoadResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busyBidId, setBusyBidId] = useState<string | null>(null);
  // CU-28 (p34) no-bids recovery.
  const [recovering, setRecovering] = useState<boolean>(false);
  const [isCancelling, setIsCancelling] = useState(false);
  // CU-27. Postponement keeps the load, its bids and its history. The
  // cancel+repost path further down creates a NEW load and loses all three,
  // leaving a cancellation on the shipper's record they never intended.
  const [showPostpone, setShowPostpone] = useState(false);
  const [postponeStart, setPostponeStart] = useState("");
  const [postponeEnd, setPostponeEnd] = useState("");
  const [postponeReason, setPostponeReason] = useState("");
  const [isPostponing, setIsPostponing] = useState(false);
  const [postponeError, setPostponeError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [acceptingBid, setAcceptingBid] = useState<Bid | null>(null);

  async function refresh(signal?: AbortSignal): Promise<void> {
    if (accessToken === null || loadId === undefined) {return;}
    try {
      const [l, b] = await Promise.all([
        loadsApi.detail(accessToken, loadId, signal),
        loadsApi.bids(accessToken, loadId, signal),
      ]);
      setLoad(l);
      setBids(b);
      setError(null);
    } catch (err) {
      if (signal?.aborted === true) {return;}
      setError(
        err instanceof ApiError ? err.problem.detail : "Couldn't load.",
      );
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    const controller = new AbortController();
    setIsLoading(true);
    void refresh(controller.signal);
    return () => { controller.abort(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, loadId]);

  function onAcceptBid(bid: Bid): void {
    // Open the payment-method picker; the actual POST fires from the
    // modal's confirm so the shipper consciously chooses cash vs online
    // (backend rejects accepts without an explicit payment_method).
    setAcceptingBid(bid);
    setActionError(null);
  }

  async function confirmAcceptBid(
    bid: Bid,
    paymentMethod: "cash" | "online",
  ): Promise<void> {
    if (accessToken === null) {return;}
    setBusyBidId(bid.id);
    setActionError(null);
    try {
      const resp = await bidsApi.accept(accessToken, bid.id, paymentMethod);
      setAcceptingBid(null);
      navigate(`/trips/${resp.trip_id}`);
    } catch (err) {
      const isInsufficient =
        err instanceof ApiError &&
        err.problem.code === "SHIPPER_INSUFFICIENT_FUNDS";
      setActionError(
        isInsufficient
          ? "Your wallet balance can't cover the platform commission. Top up your wallet and try again."
          : err instanceof ApiError
            ? err.problem.detail
            : "Couldn't accept bid.",
      );
      setAcceptingBid(null);
    } finally {
      setBusyBidId(null);
    }
  }

  /// CU-28: raise the ceiling, not the floor.
  ///
  /// `budget_max` is what a driver is bidding against; nudging
  /// `budget_min` up would only narrow the band and make the load LESS
  /// attractive. 10% is a step big enough to change who bids and small
  /// enough to click without arithmetic.
  async function onRaiseBudget(): Promise<void> {
    if (accessToken === null || load === null || loadId === undefined) {
      return;
    }
    setRecovering(true);
    setActionError(null);
    try {
      const raised = Math.round(Number.parseFloat(load.budget_max) * 1.1);
      const updated = await loadsApi.patch(accessToken, loadId, {
        budget_max: raised,
      });
      setLoad(updated);
    } catch (err) {
      setActionError(
        err instanceof ApiError
          ? err.problem.detail
          : "Couldn't update the budget.",
      );
    } finally {
      setRecovering(false);
    }
  }

  /// CU-28: give the load a longer window.
  ///
  /// `bid_window_hours` is capped at 24 server-side, so this clamps
  /// rather than sending a value the API will refuse — a shipper who
  /// already chose 22 hours gets 24 and a button that then does nothing
  /// more, instead of a validation error they cannot act on.
  async function onExtendWindow(): Promise<void> {
    if (accessToken === null || load === null || loadId === undefined) {
      return;
    }
    setRecovering(true);
    setActionError(null);
    try {
      const extended = Math.min(24, load.bid_window_hours + 6);
      if (extended === load.bid_window_hours) {
        setActionError(
          "This load already has the longest bidding window we allow (24 hours).",
        );
        return;
      }
      const updated = await loadsApi.patch(accessToken, loadId, {
        bid_window_hours: extended,
      });
      setLoad(updated);
    } catch (err) {
      setActionError(
        err instanceof ApiError
          ? err.problem.detail
          : "Couldn't extend the window.",
      );
    } finally {
      setRecovering(false);
    }
  }

  async function onRejectBid(bid: Bid): Promise<void> {
    if (accessToken === null) {return;}
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Reject ${formatINR(bid.bid_amount)}?`)) {return;}
    setBusyBidId(bid.id);
    setActionError(null);
    try {
      await bidsApi.reject(accessToken, bid.id);
      await refresh();
    } catch (err) {
      setActionError(
        err instanceof ApiError ? err.problem.detail : "Couldn't reject bid.",
      );
    } finally {
      setBusyBidId(null);
    }
  }

  async function onPostpone(): Promise<void> {
    if (loadId === undefined || accessToken === null) return;
    setIsPostponing(true);
    setPostponeError(null);
    try {
      await postponementApi.postpone(accessToken, loadId, {
        new_pickup_window_start: new Date(postponeStart).toISOString(),
        new_pickup_window_end: new Date(postponeEnd).toISOString(),
        reason: postponeReason,
      });
      setShowPostpone(false);
      setPostponeReason("");
      await refresh();
    } catch (err) {
      // POSTPONE_LIMIT_REACHED and LOAD_NOT_POSTPONABLE both carry a
      // message that explains itself; a generic string would hide the
      // limit, which is the one thing the shipper needs to know.
      setPostponeError(
        err instanceof ApiError ? err.message : "Could not postpone the load.",
      );
    } finally {
      setIsPostponing(false);
    }
  }

  async function onResume(): Promise<void> {
    if (loadId === undefined || accessToken === null) return;
    setIsPostponing(true);
    setPostponeError(null);
    try {
      await postponementApi.resume(accessToken, loadId);
      await refresh();
    } catch (err) {
      setPostponeError(
        err instanceof ApiError ? err.message : "Could not resume the load.",
      );
    } finally {
      setIsPostponing(false);
    }
  }

  async function onCancelLoad(): Promise<void> {
    if (accessToken === null || loadId === undefined) {return;}

    // FSD p130: "show cancellation charges before final customer or
    // driver confirmation". This used to say "a fee may apply" — which,
    // since D-19, was standing in for 20% of the accepted bid.
    //
    // A failed preview does NOT block the cancel. The server prices and
    // bills correctly either way, and refusing to let a shipper cancel
    // because a GET timed out would be worse than cancelling without
    // the figure in front of them.
    let feeLine = "";
    try {
      const preview = await loadsApi.cancellationPreview(accessToken, loadId);
      if (preview.is_chargeable && preview.charge_inr !== null) {
        const charge = formatINR(preview.charge_inr);
        const toDriver = Number.parseFloat(
          preview.counterparty_receives_inr,
        );
        feeLine = toDriver > 0
          // Where the money GOES matters as much as the amount: a charge
          // that compensates the driver you stood up reads very
          // differently from a penalty.
          ? `Cancelling now costs ${charge}, of which ${formatINR(preview.counterparty_receives_inr)} goes to the driver.

`
          : `Cancelling now costs ${charge}.

`;
      }
    } catch {
      // Leave `feeLine` empty — see above.
    }

    // eslint-disable-next-line no-alert
    const reason = window.prompt(
      `${feeLine}Cancellation reason (optional):`,
    );
    if (reason === null) {return;}
    setIsCancelling(true);
    setActionError(null);
    try {
      await loadsApi.cancel(accessToken, loadId, reason || undefined);
      navigate("/");
    } catch (err) {
      setActionError(
        err instanceof ApiError ? err.problem.detail : "Couldn't cancel.",
      );
    } finally {
      setIsCancelling(false);
    }
  }

  if (isLoading) {
    return (
      <div
        role="status"
        className="flex min-h-screen items-center justify-center"
      >
        <Loader2
          aria-hidden="true"
          className="h-6 w-6 animate-spin text-muted-foreground"
        />
        <span className="sr-only">Loading load…</span>
      </div>
    );
  }

  if (load === null || bids === null) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-10">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error ?? "Load not found."}</AlertDescription>
        </Alert>
        <Link to="/" className="mt-4 inline-block">
          <Button variant="outline" size="sm">
            <ArrowLeft className="h-4 w-4" />
            Back to my loads
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Load detail"
        actions={
          <Link to="/">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="h-4 w-4" />
              My loads
            </Button>
          </Link>
        }
      />

      <div className="space-y-6">
        {actionError !== null ? (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{actionError}</AlertDescription>
          </Alert>
        ) : null}

        <Card>
          <CardHeader className="flex flex-col gap-3 space-y-0 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0 flex-1 space-y-1">
              <div className="flex flex-wrap items-center gap-2">
                <span
                  className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_TONE[load.status]}`}
                >
                  {STATUS_LABEL[load.status]}
                </span>
                <span className="text-xs text-muted-foreground">
                  Posted {formatDate(load.created_at)}
                </span>
              </div>
              <CardTitle className="text-lg sm:text-xl break-words">
                {load.pickup_district} → {load.drop_district}
              </CardTitle>
              <CardDescription>
                {load.material_category} · {load.weight_kg} kg ·{" "}
                {load.cargo_length_ft != null &&
                 load.cargo_width_ft != null &&
                 load.cargo_height_ft != null ? (
                  <>
                    {/* C-23. All three or none -- a lone "8 ft" does not tell
                        a driver which way round it goes. */}
                    {load.cargo_length_ft}×{load.cargo_width_ft}×
                    {load.cargo_height_ft} ft ·{" "}
                  </>
                ) : null}
                {load.required_vehicle_type} ·{" "}
                {load.estimated_distance_km} km
              </CardDescription>
            </div>
            <div className="text-left sm:text-right shrink-0">
              <p className="text-lg font-semibold tabular-nums">
                {formatINR(load.budget_min)}–{formatINR(load.budget_max)}
              </p>
              <p className="text-xs text-muted-foreground">
                Bid window {load.bid_window_hours} h
              </p>
            </div>
          </CardHeader>
          <CardContent className="grid gap-6 sm:grid-cols-2">
            <div className="space-y-1">
              <p className="text-xs uppercase tracking-widest text-muted-foreground">
                Pickup
              </p>
              <p className="text-sm font-medium">{load.pickup_address}</p>
              <p className="text-xs text-muted-foreground">
                {load.pickup_contact_name ?? "—"} · {load.pickup_contact_phone ?? "—"}
              </p>
              <p className="text-xs text-muted-foreground">
                Window: {formatDate(load.pickup_window_start)} →{" "}
                {formatDate(load.pickup_window_end)}
              </p>
            </div>
            <div className="space-y-1">
              <p className="text-xs uppercase tracking-widest text-muted-foreground">
                Drop
              </p>
              <p className="text-sm font-medium">{load.drop_address}</p>
              <p className="text-xs text-muted-foreground">
                {load.drop_contact_name ?? "—"} · {load.drop_contact_phone ?? "—"}
              </p>
            </div>
          </CardContent>
          <CardContent className="flex flex-wrap gap-2 border-t pt-4">
            {isEditable(load) ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => { setEditing((v) => !v); }}
              >
                <Pencil className="h-4 w-4" />
                {editing ? "Cancel edit" : "Edit"}
              </Button>
            ) : null}
            {isCancellable(load) ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => void onCancelLoad()}
                disabled={isCancelling}
              >
                {isCancelling ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Ban className="h-4 w-4" />
                )}
                Cancel load
              </Button>
            ) : null}
            {/*
              CU-27. Offered ALONGSIDE cancel and before it in intent: a
              shipper whose consignment slipped a day should not have to
              throw the load away — and its bids, its negotiation and its
              history — to say so.

              Only while the load is still open. Once a bid is accepted a
              driver is committed, and moving the date under them is a
              renegotiation rather than a postponement; the server refuses
              it with LOAD_NOT_POSTPONABLE.
            */}
            {load.status === "open_for_bidding" ||
            load.status === "postponed" ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => { setShowPostpone((v) => !v); }}
              >
                <CalendarClock className="h-4 w-4" />
                {load.status === "postponed" ? "Reschedule again" : "Postpone"}
              </Button>
            ) : null}
            {load.status === "postponed" ? (
              <Button
                size="sm"
                disabled={isPostponing}
                onClick={() => void onResume()}
              >
                {isPostponing ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : null}
                Put back on the board
              </Button>
            ) : null}
            {showPostpone ? (
              <div className="mt-3 w-full space-y-3 rounded-lg border bg-muted/30 p-4">
                <p className="text-sm font-medium">Move this load's pickup</p>
                <p className="text-xs text-muted-foreground">
                  The load, its bids and its history stay. Everyone who has
                  bid is told the date moved.
                </p>
                {postponeError !== null ? (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{postponeError}</AlertDescription>
                  </Alert>
                ) : null}
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="space-y-1">
                    <Label htmlFor="postpone-start">New pickup from</Label>
                    <Input
                      id="postpone-start"
                      type="datetime-local"
                      value={postponeStart}
                      onChange={(e) => { setPostponeStart(e.target.value); }}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="postpone-end">New pickup until</Label>
                    <Input
                      id="postpone-end"
                      type="datetime-local"
                      value={postponeEnd}
                      onChange={(e) => { setPostponeEnd(e.target.value); }}
                    />
                  </div>
                </div>
                <div className="space-y-1">
                  <Label htmlFor="postpone-reason">
                    Why (shown to your bidders)
                  </Label>
                  <Input
                    id="postpone-reason"
                    value={postponeReason}
                    placeholder="Consignment not ready at the mill"
                    onChange={(e) => { setPostponeReason(e.target.value); }}
                  />
                </div>
                <Button
                  size="sm"
                  disabled={
                    isPostponing ||
                    postponeStart === "" ||
                    postponeEnd === "" ||
                    postponeReason.length < 5
                  }
                  onClick={() => void onPostpone()}
                >
                  {isPostponing ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <CalendarClock className="h-4 w-4" />
                  )}
                  Confirm new date
                </Button>
              </div>
            ) : null}
            {(load.status === "cancelled" || load.status === "expired") && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  navigate("/loads/new", { state: { repost: load } });
                }}
              >
                Repost as new load
              </Button>
            )}
          </CardContent>
        </Card>

        {editing ? (
          <EditLoadCard
            load={load}
            onCancel={() => { setEditing(false); }}
            onSaved={(updated) => {
              setLoad(updated);
              setEditing(false);
            }}
          />
        ) : null}

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Truck className="h-4 w-4" />
              Bids ({bids.total})
            </CardTitle>
            <CardDescription>
              {bids.view === "full"
                ? bids.total > bids.items.length
                  ? `Showing the ${String(bids.items.length)} best-matched of ${String(bids.total)} bids. Re-order the ones on this page below.`
                  : "Best-matched first. Re-order below."
                : "Limited bid view."}
            </CardDescription>
            {bids.view === "full" && bids.items.length > 1 ? (
              <div className="flex flex-wrap gap-2 pt-2">
                {BID_SORTS.map((opt) => (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => {
                      setBidSort(opt.key);
                    }}
                    className={
                      bidSort === opt.key
                        ? "rounded-full bg-primary px-3 py-1 text-xs font-medium text-primary-foreground"
                        : "rounded-full bg-muted px-3 py-1 text-xs font-medium text-muted-foreground hover:bg-muted/80"
                    }
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            ) : null}
          </CardHeader>
          <CardContent className="space-y-3">
            {bids.items.length === 0 ? (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  No bids yet. Drivers in your area get notified the moment
                  you post; most bids land within minutes.
                </p>
                {/* CU-28 (p34): "no-bids recovery — extend request, raise
                    budget, contact support". Every one of these was already
                    supported by PATCH /loads/{id}; nothing offered them, so
                    a shipper staring at an empty list had no next move but
                    to cancel and repost, which loses the load's identity
                    and history (CU-27's whole point).

                    Only shown while the load can still ATTRACT bids.
                    Offering "raise your budget" on a cancelled or accepted
                    load would be advice the shipper cannot act on. */}
                {load.status === "open_for_bidding" ? (
                  <div className="rounded-lg border border-dashed p-3">
                    <p className="text-sm font-medium">Not getting bids?</p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      A higher ceiling or a longer window usually helps most.
                    </p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={recovering}
                        onClick={() => {
                          void onRaiseBudget();
                        }}
                      >
                        Raise budget 10%
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={recovering}
                        onClick={() => {
                          void onExtendWindow();
                        }}
                      >
                        Give it 6 more hours
                      </Button>
                      <Button size="sm" variant="outline" asChild>
                        <Link to="/support">Contact support</Link>
                      </Button>
                    </div>
                  </div>
                ) : null}
              </div>
            ) : (
              sortBids(bids.items, bidSort).map((bid) => (
                <BidRow
                  key={bid.id}
                  bid={bid}
                  loadStatus={load.status}
                  busy={busyBidId === bid.id}
                  onAccept={() => void onAcceptBid(bid)}
                  onReject={() => void onRejectBid(bid)}
                />
              ))
            )}
          </CardContent>
        </Card>
      </div>

      {acceptingBid !== null && (
        <AcceptBidModal
          bid={acceptingBid}
          submitting={busyBidId === acceptingBid.id}
          onClose={() => { setAcceptingBid(null); }}
          onConfirm={(method) => { void confirmAcceptBid(acceptingBid, method); }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Payment-method picker on bid accept
// ---------------------------------------------------------------------------
// Mirrors the mobile-shipper PaymentMethodSheet shipped in spec4-v1
// close-out — backend rejects /bids/{id}/accept calls without an
// explicit payment_method now, so the shipper consciously picks Cash
// vs Online (wallet).
function AcceptBidModal({
  bid,
  submitting,
  onClose,
  onConfirm,
}: {
  bid: Bid;
  submitting: boolean;
  onClose: () => void;
  onConfirm: (method: "cash" | "online") => void;
}): React.JSX.Element {
  const [method, setMethod] = useState<"cash" | "online">("cash");
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Accept bid · {formatINR(bid.bid_amount)}</CardTitle>
          <CardDescription>
            Other bids will be auto-rejected and a trip will be created.
            Pick how you'll settle the trip with the driver:
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <button
            type="button"
            onClick={() => { setMethod("cash"); }}
            disabled={submitting}
            className={
              "w-full rounded-md border px-3 py-3 text-left text-sm transition-colors " +
              (method === "cash"
                ? "border-primary bg-primary/5"
                : "hover:bg-muted")
            }
          >
            <div className="font-medium">Cash on delivery</div>
            <div className="mt-1 text-xs text-muted-foreground">
              You hand the driver cash at drop-off. Platform commission is
              taken from the driver's wallet — your wallet stays untouched.
            </div>
          </button>
          <button
            type="button"
            onClick={() => { setMethod("online"); }}
            disabled={submitting}
            className={
              "w-full rounded-md border px-3 py-3 text-left text-sm transition-colors " +
              (method === "online"
                ? "border-primary bg-primary/5"
                : "hover:bg-muted")
            }
          >
            <div className="font-medium">Online (wallet)</div>
            <div className="mt-1 text-xs text-muted-foreground">
              Trip value + platform commission are pre-flighted against
              your wallet. The accept fails if your balance can't cover
              it; top up via the Wallet page first if needed.
            </div>
          </button>
          {/* Buttons stack on tiny phones (<400 px) so neither truncates,
              then sit inline + right-aligned at sm+. */}
          <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:justify-end">
            <Button variant="ghost" onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            <Button
              onClick={() => { onConfirm(method); }}
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                  Accepting…
                </>
              ) : (
                "Accept bid"
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function BidRow({
  bid,
  loadStatus,
  busy,
  onAccept,
  onReject,
}: {
  bid: Bid;
  loadStatus: LoadStatus;
  busy: boolean;
  onAccept: () => void;
  onReject: () => void;
}): React.JSX.Element {
  // Bids are only actionable while the load is genuinely open: accept_bid
  // gates on `load.status != "open_for_bidding"` (loads.py:698).
  const isOpen = loadStatus === "open_for_bidding";
  const canAct = isOpen && bid.status === "active";
  const stats = bid.bidder_stats;
  return (
    <div className="rounded-lg border bg-background p-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold">
              {formatINR(bid.bid_amount)}
            </span>
            <span className="text-xs text-muted-foreground">
              · {bid.bidder_role} · rank #{bid.bidder_rank_at_submission}
            </span>
          </div>
          <p className="text-xs text-muted-foreground">
            ETA pickup: {formatDate(bid.proposed_eta_pickup)} · expires{" "}
            {formatDate(bid.bid_expires_at)}
          </p>
          {/* CU-09. Capacity first: it is the one field that decides
              whether this truck can take the load at all, and choosing on
              price between vehicles you cannot see is not a choice. */}
          {bid.vehicle_payload_capacity_kg !== null ||
          bid.driver_name !== null ? (
            <p className="text-xs text-muted-foreground">
              {bid.driver_name ?? "Driver"}
              {bid.vehicle_display_name !== null
                ? ` · ${bid.vehicle_display_name}`
                : bid.vehicle_type !== null
                  ? ` · ${bid.vehicle_type.replace(/_/g, " ")}`
                  : ""}
              {bid.vehicle_payload_capacity_kg !== null
                ? ` · ${bid.vehicle_payload_capacity_kg.toLocaleString()} kg capacity`
                : ""}
            </p>
          ) : null}
          {bid.message_to_shipper !== null && bid.message_to_shipper.length > 0 ? (
            <p className="text-xs italic text-muted-foreground">
              "{bid.message_to_shipper}"
            </p>
          ) : null}
          {stats !== null ? (
            <p className="text-xs text-muted-foreground">
              {stats.total_trips} trips · {Math.round(stats.completion_rate * 100)}% complete
              {stats.avg_rating !== null
                ? ` · ${stats.avg_rating.toFixed(1)}★`
                : ""}
            </p>
          ) : null}
        </div>
        {canAct ? (
          // Phone (<sm): buttons sit in a row, each grows to share width.
          // sm+: stack vertically on the right edge of the card.
          <div className="flex flex-row gap-2 sm:flex-col sm:shrink-0">
            <Button size="sm" onClick={onAccept} disabled={busy} className="flex-1 sm:flex-none">
              {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
              Accept
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={onReject}
              disabled={busy}
              className="flex-1 sm:flex-none"
            >
              <ThumbsDown className="h-3 w-3" />
              Reject
            </Button>
          </div>
        ) : (
          <span className="text-xs text-muted-foreground">{bid.status}</span>
        )}
      </div>
    </div>
  );
}

function EditLoadCard({
  load,
  onCancel,
  onSaved,
}: {
  load: Load;
  onCancel: () => void;
  onSaved: (load: Load) => void;
}): React.JSX.Element {
  const { accessToken } = useAuth();
  // Prefix for the label/control associations below. useId keeps them unique
  // even if this card is ever rendered more than once on a page.
  const uid = useId();
  const [pickupAddress, setPickupAddress] = useState(load.pickup_address);
  const [pickupContactName, setPickupContactName] = useState(load.pickup_contact_name ?? "");
  const [pickupContactPhone, setPickupContactPhone] = useState(load.pickup_contact_phone ?? "");
  const [dropAddress, setDropAddress] = useState(load.drop_address);
  const [dropContactName, setDropContactName] = useState(load.drop_contact_name ?? "");
  const [dropContactPhone, setDropContactPhone] = useState(load.drop_contact_phone ?? "");
  const [budgetMin, setBudgetMin] = useState(load.budget_min);
  const [budgetMax, setBudgetMax] = useState(load.budget_max);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  function diff(orig: string, next: string): string | undefined {
    return orig === next ? undefined : next;
  }
  function diffNum(orig: string, next: string): number | undefined {
    const n = Number.parseFloat(next);
    if (!Number.isFinite(n)) {return undefined;}
    return orig === next ? undefined : n;
  }

  async function onSave(): Promise<void> {
    if (accessToken === null) {return;}
    const body: LoadPatchPayload = {};
    const pAddr = diff(load.pickup_address, pickupAddress);
    if (pAddr !== undefined) {body.pickup_address = pAddr;}
    const pName = diff(load.pickup_contact_name ?? "", pickupContactName);
    if (pName !== undefined) {body.pickup_contact_name = pName;}
    const pPhone = diff(load.pickup_contact_phone ?? "", pickupContactPhone);
    if (pPhone !== undefined) {body.pickup_contact_phone = pPhone;}
    const dAddr = diff(load.drop_address, dropAddress);
    if (dAddr !== undefined) {body.drop_address = dAddr;}
    const dName = diff(load.drop_contact_name ?? "", dropContactName);
    if (dName !== undefined) {body.drop_contact_name = dName;}
    const dPhone = diff(load.drop_contact_phone ?? "", dropContactPhone);
    if (dPhone !== undefined) {body.drop_contact_phone = dPhone;}
    const bMin = diffNum(load.budget_min, budgetMin);
    if (bMin !== undefined) {body.budget_min = bMin;}
    const bMax = diffNum(load.budget_max, budgetMax);
    if (bMax !== undefined) {body.budget_max = bMax;}

    if (Object.keys(body).length === 0) {
      onCancel();
      return;
    }

    setSaving(true);
    setErr(null);
    try {
      const updated = await loadsApi.patch(accessToken, load.id, body);
      onSaved(updated);
    } catch (e) {
      setErr(e instanceof ApiError ? e.problem.detail : "Couldn't save.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Pencil className="h-4 w-4" />
          Edit load
        </CardTitle>
        <CardDescription>
          Only editable fields are shown. Lat/lng + cargo can't be changed —
          cancel and repost instead.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4 md:grid-cols-2">
        <div className="space-y-2 md:col-span-2">
          <Label htmlFor={`${uid}-pickup-address`}>Pickup address</Label>
          <Input id={`${uid}-pickup-address`} value={pickupAddress} onChange={(e) => { setPickupAddress(e.target.value); }} />
        </div>
        <div className="space-y-2">
          <Label htmlFor={`${uid}-pickup-contact-name`}>Pickup contact name</Label>
          <Input id={`${uid}-pickup-contact-name`} value={pickupContactName} onChange={(e) => { setPickupContactName(e.target.value); }} />
        </div>
        <div className="space-y-2">
          <Label htmlFor={`${uid}-pickup-contact-phone`}>Pickup contact phone</Label>
          <Input id={`${uid}-pickup-contact-phone`} value={pickupContactPhone} onChange={(e) => { setPickupContactPhone(e.target.value); }} />
        </div>
        <div className="space-y-2 md:col-span-2">
          <Label htmlFor={`${uid}-drop-address`}>Drop address</Label>
          <Input id={`${uid}-drop-address`} value={dropAddress} onChange={(e) => { setDropAddress(e.target.value); }} />
        </div>
        <div className="space-y-2">
          <Label htmlFor={`${uid}-drop-contact-name`}>Drop contact name</Label>
          <Input id={`${uid}-drop-contact-name`} value={dropContactName} onChange={(e) => { setDropContactName(e.target.value); }} />
        </div>
        <div className="space-y-2">
          <Label htmlFor={`${uid}-drop-contact-phone`}>Drop contact phone</Label>
          <Input id={`${uid}-drop-contact-phone`} value={dropContactPhone} onChange={(e) => { setDropContactPhone(e.target.value); }} />
        </div>
        <div className="space-y-2">
          <Label htmlFor={`${uid}-budget-min`}>Min budget (₹)</Label>
          <Input
            id={`${uid}-budget-min`}
            inputMode="numeric"
            value={budgetMin}
            onChange={(e) => { setBudgetMin(e.target.value.replace(/[^\d.]/g, "")); }}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor={`${uid}-budget-max`}>Max budget (₹)</Label>
          <Input
            id={`${uid}-budget-max`}
            inputMode="numeric"
            value={budgetMax}
            onChange={(e) => { setBudgetMax(e.target.value.replace(/[^\d.]/g, "")); }}
          />
        </div>
        {err !== null ? (
          <Alert variant="destructive" className="md:col-span-2">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{err}</AlertDescription>
          </Alert>
        ) : null}
      </CardContent>
      <CardContent className="flex justify-end gap-2 border-t pt-4">
        <Button variant="ghost" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button onClick={() => void onSave()} disabled={saving}>
          {saving ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Saving…
            </>
          ) : (
            <>
              <ChevronRight className="h-4 w-4" />
              Save changes
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
}
